<template>
  <div class="workflow-mobile-view">
    <WorkflowFiltersMenu
      v-if="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="workflowFilters"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @close="showFilters = false"
    />
    <section class="filter-section" data-cy="filter-section">
      <div class="sort-wrapper">
        <DropDownList
          class="sort-dropdown"
          :textField="'text'"
          :data-items="sortOptions"
          v-model="selectedSortOption"
          @change="onSortChanged"
          data-cy="sort-dropdown"
        />
        <div class="sort-dir" @click="sortAscending = !sortAscending">
          <SortIcon
            :ascending="sortAscending"
            fill="grey"
            activeFill="#399CD3"
          />
        </div>
      </div>
      <div class="filter-wrapper">
        <span
          class="filter-icon"
          @click="showFilters = true"
          data-cy="filters-button"
        >
          <FilterIcon />
        </span>
      </div>
    </section>

    <section
      class="workflow-mobile-view__actions"
      data-cy="workflow-mobile-view-actions"
    >
      <div v-if="dataRangeVisible" class="container-filter-wrapper">
        <DateRangePicker
          class="container-date-picker"
          :value="dateRange"
          @change="handleChange"
          data-cy="date-range-picker"
        />
        <Button
          :theme-color="'primary'"
          type="button"
          :disabled="workflowsLoading"
          @click="filterByDate"
          data-cy="search-date-button"
          >By Date</Button
        >
      </div>
      <div class="container-filter-wrapper">
        <input
          type="text"
          v-model="jobId"
          class="job-id-input"
          placeholder="Search by Job ID"
          @keydown.enter.prevent="filterByJobId"
          data-cy="job-id-input"
        />
        <Button
          :theme-color="'primary'"
          type="button"
          :disabled="workflowsLoading"
          @click="filterByJobId"
          data-cy="search-job-id-button"
          >By Job ID</Button
        >
      </div>
    </section>

    <WorkflowList
      :workflows="workflows"
      :totalWorkflows="totalWorkflows"
      :defaultTake="workflowsTake"
      :skip="workflowsSkip"
      :loading="workflowsLoading"
      @workflowsChanged="onWorkflowsChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import WorkflowList from "./WorkflowList.vue";
import FilterIcon from "@/icons/FilterIcon.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import enumHelper from "@/shared/helpers/enumHelper";
import { Button } from "@progress/kendo-vue-buttons";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  FilterValuesTake,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import type { GetWorkflowsWithFilteringRequest } from "@/api/workflowOverview";
import { EnumWorkflowMode } from "@/Domain/EnumWorkflowMode";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { FilterField } from "@/Domain/FilterField";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { FilterExpression } from "@/Domain/FilterExpression";
import DateRangePicker from "@/shared/ui/inputs/DateRangePicker.vue";
import WorkflowFiltersMenu from "./WorkflowFiltersMenu.vue";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";
import { EnumSubmissionType } from "@/Domain/EnumSubmissionType";
import SortIcon from "@/icons/SortIcon.vue";

enum SearchMode {
  ByDate = "SearchByDateRange",
  ByJobId = "SearchByJobId",
}

const props = defineProps<{
  mode: EnumWorkflowMode;
}>();

const workflows = ref<WfSubmissionProductAndIngredient[]>([]);
const totalWorkflows = ref<number>(0);
const workflowsLoading = ref<boolean>(false);
const workflowsTake = ref<number>(25);
const workflowsSkip = ref<number>(0);
const showFilters = ref<boolean>(false);
const workflowFilters = ref<FilterMenuItem[]>([]);
const sortAscending = ref<boolean>(false);
const selectedSearchMode = ref<SearchMode>(SearchMode.ByDate);
const jobId = ref<string>("");

const currentDate = new Date();
const startDate = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() - 1,
  currentDate.getDate()
);

const myWorkDefaultStartDate = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() - 5,
  currentDate.getDate()
);

const dateRange = ref({
  start: startDate,
  end: currentDate,
} as SelectionRange);

// Hide the date picker for 'my work'.
// Show all work assigned to the person if it's open or in progress
const dataRangeVisible = computed(() => {
  return props.mode != EnumWorkflowMode.My;
});

const sortOptions = ref([
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Urgency",
    value: "IsUrgent",
  },
  {
    text: "Job ID",
    value: "JobId",
  },
  {
    text: "Company",
    value: "CompanyName",
  },
  {
    text: "Date Modified",
    value: "LastModified",
  },
]);

const selectedSortOption = ref<{ text: string; value: string }>(
  sortOptions.value[0]
);

const valueFiltersConvertOptions = [
  {
    column: "JobType",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumSubmissionType[value]);
    },
  },
  {
    column: "Status",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumWorkflowStatus[value]);
    },
  },
] as ValueFieldConvertOption[];

const getWorkflowWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  workflowFilters.value.forEach((f) => {
    const filterGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: f.field,
      InternalFieldName: "",
    } as FilterGroup;
    const filterField = {
      Operator: f.getOpValue ? f.getOpValue(f.value) : "eq",
      Value: f.getFilterValue ? f.getFilterValue(f.value) : f.value,
    } as FilterField;
    filterGroup.FilterFields.push(filterField);

    filterModel.FilterGroups.push(filterGroup);
  });

  const requestStartDate =
    props.mode == EnumWorkflowMode.My
      ? myWorkDefaultStartDate
      : dateRange.value.start;

  const requestParams = {
    beginDate:
      selectedSearchMode.value != SearchMode.ByDate
        ? ""
        : requestStartDate?.toLocaleDateString("en-US"),
    endDate:
      selectedSearchMode.value != SearchMode.ByDate
        ? ""
        : dateRange.value.end?.toLocaleDateString("en-US"),
    mode: props.mode.toString(),
    searchMode: selectedSearchMode.value,
    jobId: selectedSearchMode.value == SearchMode.ByJobId ? jobId.value : "",
    filters: JSON.stringify(filterModel),
    skip: workflowsSkip.value,
    take: workflowsTake.value,
    valuesForFieldOnly: valuesForFieldOnly,
    sortField: selectedSortOption.value.value,
    ascending: sortAscending.value,
  } as GetWorkflowsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetWorkflowsWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetWorkflowsWithFiltering", requestParams)
  );
};

const getWorkflowsAsync = async function (append: boolean = false) {
  if (workflowsLoading.value) return;
  workflowsLoading.value = true;
  if (!append) workflows.value = [];

  await handleResponsePromiseAsync(
    getWorkflowWithFilteringAsync(),
    (data) => {
      if (!append) workflows.value = data.ListItems;
      else workflows.value = [...workflows.value, ...data.ListItems];
      totalWorkflows.value = data.TotalItemsInList;
    },
    "WorkflowMobileView.vue",
    "getWorkflowsAsync"
  );

  workflowsLoading.value = false;
};

const getPropValuesAsync = async function (field: string) {
  let result: WfSubmissionProductAndIngredient[] = [];
  await handleResponsePromiseAsync(
    getWorkflowWithFilteringAsync(field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, field, result);
    },
    "WorkflowMobileView.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onWorkflowsChanged = function (skip: number, take: number) {
  workflowsTake.value = take;
  workflowsSkip.value = skip;
  getWorkflowsAsync(true);
};

const onFiltersApplied = function (filters: FilterMenuItem[]) {
  workflowFilters.value = filters;
  workflowsSkip.value = 0;
  getWorkflowsAsync();
};

const onFiltersCleared = function () {
  workflowFilters.value = [];
  workflowsSkip.value = 0;
  getWorkflowsAsync();
};

const filterByDate = function () {
  selectedSearchMode.value = SearchMode.ByDate;
  workflowsSkip.value = 0;
  getWorkflowsAsync();
};

const filterByJobId = function () {
  selectedSearchMode.value = SearchMode.ByJobId;
  workflowsSkip.value = 0;
  getWorkflowsAsync();
};

const handleChange = function (newRange: SelectionRange) {
  dateRange.value = newRange;
};

const onSortChanged = function () {
  workflowsSkip.value = 0;
  getWorkflowsAsync();
};

watch(
  () => sortAscending.value,
  () => {
    onSortChanged();
  }
);

onBeforeMount(async () => {
  await getWorkflowsAsync();
});
</script>

<style lang="scss">
.workflow-mobile-view {
  .container-date-picker {
    .k-dateinput {
      width: 120px;
      font-size: 13px;
      height: 32px;

      input {
        padding: 8px;
      }
    }
  }
}
</style>

<style scoped lang="scss">
.workflow-mobile-view {
  flex: 1 0 auto;

  &__actions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px;

    .container-filter-wrapper {
      display: flex;
      gap: 16px;
      align-items: flex-end;
      flex-wrap: wrap;
    }

    .container-text {
      height: 32px;
      display: inline-flex;
      align-items: center;
    }
  }

  .filter-section {
    background: #222;
    border-top: 1px solid #333;
    display: flex;
    align-items: center;
    padding: 10px 15px;
    justify-content: space-between;
    gap: 12px;

    .sort-wrapper {
      flex: 1;
      max-width: 320px;
      display: flex;
      gap: 8px;
      align-items: center;
    }
  }

  .job-id-input {
    width: 120px;
    font-size: 13px;
  }
}

// items
.items-section {
  padding: 20px 15px;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
